// Firebase 초기화 및 메시징 설정
import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { firebaseConfig } from '@/config/firebase.config';

let app: ReturnType<typeof initializeApp> | null = null;
let messaging: ReturnType<typeof getMessaging> | null = null;

// Firebase 앱 초기화
const initializeFirebase = () => {
  if (app) {
    return app;
  }

  const existingApps = getApps();
  if (existingApps.length > 0) {
    app = existingApps[0];
  } else {
    try {
      app = initializeApp(firebaseConfig);
    } catch (error) {
      console.error('❌ Firebase 앱 초기화 실패:', error);
      throw error;
    }
  }

  return app;
};

// Firebase Messaging 초기화
const initializeMessaging = async () => {
  if (messaging) {
    return messaging;
  }

  if (typeof window === 'undefined') {
    console.warn('⚠️ 서버 사이드에서는 Messaging을 초기화할 수 없습니다.');
    return null;
  }

  try {
    const app = initializeFirebase();
    messaging = getMessaging(app);
    return messaging;
  } catch (error) {
    console.error('❌ Firebase Messaging 초기화 실패:', error);
    if (error instanceof Error) {
      console.error('에러 메시지:', error.message);
      console.error('에러 스택:', error.stack);
    }
    return null;
  }
};

// FCM 토큰 가져오기
// sw.js를 사용합니다
export const getFCMToken = async (): Promise<string | null> => {
  try {
    const messagingInstance = await initializeMessaging();
    if (!messagingInstance) {
      console.warn('❌ Firebase Messaging이 초기화되지 않았습니다.');
      return null;
    }

    // VAPID 키는 Firebase Console > Project Settings > Cloud Messaging > Web Push certificates에서 생성
    const vapidKey = firebaseConfig.vapidKey;
    if (!vapidKey) {
      console.error('❌ VAPID 키가 설정되지 않았습니다.');
      return null;
    }

    // 우리가 등록한 sw.js Service Worker를 사용
    const swRegistration = await navigator.serviceWorker.ready;

    const token = await getToken(messagingInstance, {
      vapidKey,
      serviceWorkerRegistration: swRegistration, // 명시적으로 sw.js 사용
    });

    if (token) {
      return token;
    } else {
      console.warn('FCM token not found, permission is required');
      return null;
    }
  } catch (error) {
    console.error('FCM token retrieval failed:', error);
    if (error instanceof Error) {
      console.error('에러 메시지:', error.message);
      console.error('에러 스택:', error.stack);
    }
    return null;
  }
};

// 포그라운드 메시지 수신 처리 (콜백 방식)
// 동기적으로 cleanup 함수를 반환, 비동기 등록 전 cleanup 호출 시 등록 자체를 방지
export const onMessageListener = (
  callback: (payload: any) => void,
): (() => void) => {
  let unsubscribe: (() => void) | null = null;
  let cancelled = false;

  initializeMessaging()
    .then(messagingInstance => {
      if (cancelled) return;
      if (messagingInstance) {
        unsubscribe = onMessage(messagingInstance, payload => {
          callback(payload);
        });
      } else {
        console.warn('Messaging instance not found, cannot register foreground listener');
      }
    })
    .catch(error => {
      console.error('Foreground message listener registration failed:', error);
    });

  return () => {
    cancelled = true;
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  };
};
